// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getOrganizationUsers } from '@/account/api/get-organization-users';
import { Person } from '@/account/api/types';
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormPersonSearch } from '@/components/form-person-search';
import { FormTextField } from '@/components/form-text-field';
import { SupportAlert } from '@/components/support-alert';
import { SupportSnackbar } from '@/components/support-snackbar';
import { createCost } from '@/costs/api/create-cost';
import { updateCost } from '@/costs/api/update-cost';
import { FormCostCategoryAutocomplete } from '@/costs/components/form-cost-category-autocomplete';
import {
  Currency,
  SerializableCurrency,
  currencyInputSchema,
} from '@/currency/api/types';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { ProjectAutocomplete } from '@/project/components/project-autocomplete';
import { useSparseProjectList } from '@/project/hooks/use-sparse-project-list';
import { SparseProject } from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  costIncurredOn: dateSchema,
  amount: currencyInputSchema.refine(
    // An amount that failed to parse already carries its own issue.
    (value) => !Decimal.isDecimal(value.amount) || !value.amount.isNegative(),
    { path: ['amount'], message: 'Amount cannot be negative' }
  ),
  category: z.string().nullable(),
  project: z.custom<SparseProject>().nullable(),
  person: z.custom<Person>().nullable(),
});

interface FormData {
  description: string;
  costIncurredOn: DateTime;
  amount: SerializableCurrency;
  category: string | null;
  project: SparseProject | null;
  person: Person | null;
}

type FormOutput = z.output<typeof formSchema>;

interface Props {
  /**
   * The project to pre-fill in the project selector.
   * If provided, this project will be selected by default but can be changed.
   */
  initialProject?: SparseProject;

  /**
   * The cost to edit. If provided, dialog is in edit mode.
   */
  initialCost?: {
    key: string;
    amount?: Currency | null;
    description?: string;
    category?: string | null;
    costIncurredOn: DateTime;
    project?: SparseProject;
    person?: Person;
  };

  /**
   * If true, the dialog is open.
   */
  isOpen: boolean;

  /**
   * A callback fired when the dialog requests to be closed.
   */
  onClose: () => void;

  /**
   * Additional query keys to invalidate on successful cost creation.
   */
  invalidationKeys?: string[][];
}

/**
 * A dialog for creating or editing a cost with a description and amount.
 */
export function CostDialog(props: Props) {
  const queryClient = useQueryClient();
  const isEditing = !!props.initialCost;

  // Get current user for default person selection
  const { user: currentUser } = useCurrentUser();

  // Memoize today to prevent constant recalculation
  const today = useMemo(() => DateTime.now().startOf('day'), []);

  // Get projects for the autocomplete
  const { projects: allProjects, isLoading: isLoadingProjects } =
    useSparseProjectList();

  // Get organization users to validate if current user is a member
  const usersQuery = useQuery({
    queryKey: ['organization-users', currentUser?.organization.key],
    queryFn: () => getOrganizationUsers(currentUser!.organization.key),
    enabled: !!currentUser,
  });

  // Convert current user to person format for the person field, but only if they exist in organization users
  const currentUserAsPerson = useMemo((): Person | null => {
    if (!currentUser || !usersQuery.data) return null;

    // Check if current user exists in the organization users list
    const userExistsInOrg = usersQuery.data.users.some(
      (orgUser) => orgUser.id === currentUser.id && !orgUser.isDeactivated
    );

    // Only return the current user as person if they exist in the organization
    if (userExistsInOrg) {
      return {
        id: currentUser.id,
        key: currentUser.key,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
      };
    }

    return null;
  }, [currentUser, usersQuery.data]);

  // Calculate default values that only change when props change
  const defaultValues = useMemo((): FormData => {
    const baseValues = {
      amount: toCurrencyInputValue(
        props.initialCost?.amount,
        currentUser.organization.defaultCurrency
      ),
      description: props.initialCost?.description || '',
      category: props.initialCost?.category || null,
      costIncurredOn: props.initialCost?.costIncurredOn || today,
    };

    if (isEditing) {
      return {
        ...baseValues,
        project: props.initialCost?.project || null,
        person: props.initialCost?.person || null,
      };
    }

    return {
      ...baseValues,
      project: props.initialCost?.project || props.initialProject || null,
      person: props.initialCost?.person || currentUserAsPerson || null,
    };
  }, [
    isEditing,
    props.initialCost?.amount,
    props.initialCost?.description,
    props.initialCost?.category,
    props.initialCost?.costIncurredOn,
    props.initialCost?.project,
    props.initialCost?.person,
    props.initialProject,
    currentUserAsPerson,
    currentUser.organization.defaultCurrency,
    today,
  ]);

  // Form error message for inline display (handled backend errors)
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  // Snackbar for unhandled errors (malformed response or server error)
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  const costMutation = useMutation({
    mutationFn: (data: FormOutput) => {
      const payload = {
        amount: data.amount,
        description: data.description,
        category: data.category ?? undefined,
        costIncurredOn: data.costIncurredOn,
        projectId: data.project?.id ?? null,
        personId: data.person?.id ?? null,
      };

      if (isEditing && props.initialCost) {
        return updateCost(props.initialCost.key, payload);
      }
      return createCost(payload);
    },
    onSuccess: (resp) => {
      // Invalidate default cost queries
      queryClient.invalidateQueries({
        queryKey: ['costs'],
      });

      // Invalidate any additional keys provided
      if (props.invalidationKeys) {
        props.invalidationKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }

      // Reset form with the server response values
      reset({
        amount: toCurrencyInputValue(
          resp.cost.amount,
          currentUser.organization.defaultCurrency
        ),
        description: resp.cost.description || '',
        category: resp.cost.category || null,
        costIncurredOn: resp.cost.costIncurredOn,
        person: resp.cost.attachedPerson || null,
        project: resp.cost.attachedProject || null,
      });
      props.onClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error as AxiosError);
      // Reset both surfaces before branching so a retry never leaves
      // stale state from the previous attempt rendered alongside the
      // new one.
      setFormErrorMessage(null);
      setShowErrorSnackbar(false);
      if (isPlanLimitError(error)) {
        return;
      }

      if (apiError.apiStatusCode === ERROR_CODE_MAPPING.cost_invalid_date) {
        setError('costIncurredOn', {
          type: 'custom',
          message: 'Date cannot be in the future',
        });
        return;
      }
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setFormErrorMessage(curated);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setShowErrorSnackbar(true);
      }
    },
  });

  const { control, reset, setError, handleSubmit, formState } = useForm<
    FormData,
    unknown,
    FormOutput
  >({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Track the last props we used to reset the form
  const lastResetPropsRef = useRef<string>('');

  // Create a stable string representation of the relevant props
  // This allows us to properly handle resetting the form ONLY if the absolute state changes from props.
  // Without this, any changes to the underlying props can cause a relativistic change due to the memory being swapped.
  const propsKey = useMemo(() => {
    return JSON.stringify({
      isOpen: props.isOpen,
      initialCostKey: props.initialCost?.key,
      initialCostAmount: props.initialCost?.amount
        ? `${props.initialCost.amount.amount.toString()} ${props.initialCost.amount.isoCode}`
        : null,
      initialCostDescription: props.initialCost?.description,
      initialCostCategory: props.initialCost?.category,
      initialCostDate: props.initialCost?.costIncurredOn?.toISODate(),
      initialProjectId:
        props.initialCost?.project?.id || props.initialProject?.id,
      initialPersonId: props.initialCost?.person?.id,
      defaultPersonId: isEditing ? undefined : currentUserAsPerson?.id,
    });
  }, [
    isEditing,
    props.isOpen,
    props.initialCost?.key,
    props.initialCost?.amount,
    props.initialCost?.description,
    props.initialCost?.category,
    props.initialCost?.costIncurredOn,
    props.initialCost?.project?.id,
    props.initialCost?.person?.id,
    props.initialProject?.id,
    currentUserAsPerson?.id,
  ]);

  useEffect(() => {
    // Only reset if:
    // 1. Dialog is open
    // 2. Props have actually changed
    // 3. User hasn't made any changes yet (form is not dirty)
    if (
      props.isOpen &&
      propsKey !== lastResetPropsRef.current &&
      !formState.isDirty
    ) {
      reset(defaultValues);
      lastResetPropsRef.current = propsKey;
    }
  }, [props.isOpen, propsKey, defaultValues, reset, formState.isDirty]);

  // Clear the last reset props when dialog closes
  useEffect(() => {
    if (!props.isOpen) {
      lastResetPropsRef.current = '';
    }
  }, [props.isOpen]);

  const close = () => {
    props.onClose();
    reset(defaultValues);
    setFormErrorMessage(null);
    setShowErrorSnackbar(false);
  };

  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    costMutation.mutate(data);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={showErrorSnackbar}
        message={`An error occurred while ${isEditing ? 'updating' : 'adding'} the cost.`}
        onClose={() => setShowErrorSnackbar(false)}
      />
      <DialogTitle>{isEditing ? 'Edit cost' : 'Add cost'}</DialogTitle>
      <DialogContent dividers>
        {formErrorMessage && (
          <Box sx={{ mb: 3 }}>
            <SupportAlert message={formErrorMessage} />
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Stack spacing={2}>
              <FormDatePicker
                disableFuture
                control={control}
                name={'costIncurredOn'}
                label={'Date'}
              />

              <FormTextField
                name="description"
                label="Description"
                control={control}
              />
              <FormCurrencyTextField
                name="amount"
                label="Amount"
                placeholder="0.00"
                fullWidth
                control={control}
              />
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack spacing={2}>
              <FormPersonSearch
                name="person"
                control={control}
                label="Person"
                placeholder="Select person (optional)"
              />
              <Controller
                name="project"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ProjectAutocomplete
                    size="small"
                    label="Project"
                    placeholder="Select project (optional)"
                    options={allProjects}
                    value={value}
                    onChange={(_event, newValue) => {
                      onChange(newValue);
                    }}
                    loading={isLoadingProjects}
                    filterOptions={(options, state) => {
                      // Hide archived projects when no search input
                      if (state.inputValue === '') {
                        return options.filter((option) => !option.isArchived);
                      }

                      const lowerSearchTerm = state.inputValue.toLowerCase();
                      const showArchived = lowerSearchTerm === 'archived';

                      // Filter options based on search input
                      return options.filter((option) => {
                        const labelMatches = option.name
                          .toLowerCase()
                          .includes(lowerSearchTerm);
                        const isArchivedMatch =
                          showArchived && option.isArchived;

                        return labelMatches || isArchivedMatch;
                      });
                    }}
                  />
                )}
              />
              <FormCostCategoryAutocomplete
                name="category"
                control={control}
                label="Category"
                placeholder="Select category (optional)"
              />
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
        >
          {isEditing ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
